import React, { useRef, useState } from "react";
import { Button, Divider, Flex, Modal } from "antd";
import { DownOutlined, StarFilled } from "@ant-design/icons";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import ImageCarousel from "../../components/shared/ImageCarousel";
import { ITINERARY_TIMELINE_COPY, ItineraryLanguage } from "../../components/shared/itineraryTimelineCopy";
import { ItineraryActivity, ItineraryHotel, ItineraryPlan } from "../../interfaces/itinerary.interface";

const PRIMARY_COLOR = "#DC1A22";

// MOB / MOB (Light Snack) / Afternoon Tea / MOB (Light Snack) / MOB (Light Snack)
const MEAL_CN: Record<string, string> = {
  Breakfast: "早餐",
  Lunch: "午餐",
  Dinner: "晚餐",
  MOB: "机上用餐",
  MGD: "米其林指南餐",
  "MOB (Light Snack)": "机上用餐（小食）",
  "Afternoon Tea": "下午茶",
  "Supper": "夜宵",
  "-": "-",
};

interface ModalState {
  type: "activity" | "hotel";
  title: string;
  tagline?: string;
  description?: string;
  stars?: number;
  images: string[];
  initialSlide?: number;
}

interface Props {
  plans: ItineraryPlan[];
  generalContent?: string;
  generalContentTwo?: string;
  additionalTour?: string;
  optionalTour?: string;
  specialDisclaimer?: string;
  specialDisclaimerTwo?: string;
  isPreview?: boolean;
  previewStatus?: "ready_to_review" | "ready_to_approve" | null;
}

const PREVIEW_BANNER_TEXT: Record<string, string> = {
  ready_to_review: "Preview — this itinerary is ready for review and not yet published to customers.",
  ready_to_approve: "Preview — this itinerary is ready to approve and not yet published to customers.",
};
const DEFAULT_PREVIEW_BANNER_TEXT = "Preview — this itinerary is pending final review and is not yet published to customers.";

const Stars: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? (
    <span className="hotel-card__stars">
      {Array.from({ length: count }).map((_, i) => (
        <StarFilled key={i} />
      ))}
    </span>
  ) : null;

const ItineraryTab: React.FC<Props> = ({
  plans,
  generalContent,
  generalContentTwo,
  additionalTour,
  optionalTour,
  specialDisclaimer,
  specialDisclaimerTwo,
  isPreview,
  previewStatus,
}) => {
  const [lang, setLang] = useState<ItineraryLanguage>("en");
  const [openPanels, setOpenPanels] = useState<string[]>(() => (plans[0] ? [String(plans[0].day)] : []));
  const [modal, setModal] = useState<ModalState | null>(null);
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const allKeys = plans.map((p) => String(p.day));
  const allExpanded = plans.length > 0 && openPanels.length === plans.length;

  const toggleShowAll = () => setOpenPanels(allExpanded ? [] : allKeys);

  const scrollDayIntoView = (key: string) => {
    const el = panelRefs.current[key];
    if (!el) return;
    const nav = document.querySelector("#tour-details-tabs .ant-tabs-nav") as HTMLElement | null;
    const offset = 54 + (nav?.offsetHeight ?? 0) + 8; // 54px fixed site header
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const togglePanel = (key: string) => {
    setOpenPanels((prev) => {
      const isOpen = prev.includes(key);
      if (isOpen) return prev.filter((k) => k !== key);
      setTimeout(() => scrollDayIntoView(key), 50);
      return [key];
    });
  };

  const t = (en?: string, cn?: string) => (lang === "cn" && cn ? cn : en ?? "");
  const copy = ITINERARY_TIMELINE_COPY[lang];

  const getImages = (item: ItineraryActivity | ItineraryHotel) => {
    const imagesTwo = (item as any).images_two as string[] | undefined;
    return lang === "cn" && imagesTwo?.length ? imagesTwo : (item.images ?? []);
  };

  const hasActivityModalContent = (activity: ItineraryActivity) =>
    (getImages(activity).length > 0) || !!activity.description || !!activity.description_two;

  const hasHotelModalContent = (hotel: ItineraryHotel) =>
    (getImages(hotel).length > 0) || !!hotel.description || !!hotel.description_two;

  const openActivityModal = (activity: ItineraryActivity, imageIndex = 0) =>
    setModal({
      type: "activity",
      title: t(activity.title, activity.title_two),
      tagline: t(activity.tagline, activity.tagline_two),
      description: t(activity.description, activity.description_two),
      images: getImages(activity),
      initialSlide: imageIndex,
    });

  const openHotelModal = (hotel: ItineraryHotel) =>
    setModal({
      type: "hotel",
      title: t(hotel.name, hotel.name_two),
      stars: parseInt(hotel.category ?? "0", 10) || 0,
      description: t(hotel.description, hotel.description_two),
      images: getImages(hotel),
    });

  const renderMealBadge = (displayMeals: string) => {
    const label = lang === "cn"
      ? displayMeals.split(" / ").map((t) => MEAL_CN[t] ?? t).join(" / ")
      : displayMeals;
    return <span className="day-badge active meal-badge">{label}</span>;
  };

  const hasMOB = plans.some((p) => p.display_meals?.includes("MOB"));
  const hasMGD = plans.some((p) => p.display_meals?.includes("MGD"));

  return (
    <div className="itinerary-tab">
      {isPreview && (
        <div className="itinerary-tab__preview-banner">
          {(previewStatus && PREVIEW_BANNER_TEXT[previewStatus]) || DEFAULT_PREVIEW_BANNER_TEXT}
        </div>
      )}
      {/* Section header */}
      <Flex justify="space-between" align="center" className="itinerary-tab__header">
        <Flex align="center" gap={10}>
          <h3 className="itinerary-tab__heading">{plans.length} Day Itinerary</h3>
          <div className="itinerary-tab__lang-toggle">
            <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>{ITINERARY_TIMELINE_COPY.en.language}</button>
            <span className="divider">|</span>
            <button className={lang === "cn" ? "active" : ""} onClick={() => setLang("cn")}>{ITINERARY_TIMELINE_COPY.cn.language}</button>
          </div>
        </Flex>
        <Button type="link" onClick={toggleShowAll} style={{ color: PRIMARY_COLOR, padding: 0 }}>
          {allExpanded ? "Collapse All" : "Show All"}
        </Button>
      </Flex>

      {/* Days — vertical stepper */}
      <div className="itinerary-stepper">
        {plans.map((plan: ItineraryPlan, idx: number) => {
          const showMeals = plan.display_meals && plan.display_meals !== "N/A";
          const isLast = idx === plans.length - 1;
          const isOpen = openPanels.includes(String(plan.day));

          return (
            <div key={plan.day} className="stepper-step">
              {/* Left: dot + connector line */}
              <div className="stepper-step__indicator">
                <div className="stepper-step__dot" />
                {!isLast && <div className="stepper-step__line" />}
              </div>

              {/* Right: collapsible panel */}
              <div
                className="stepper-step__content"
                ref={(el) => { panelRefs.current[String(plan.day)] = el; }}
              >
                <div
                  className="stepper-step__header"
                  onClick={() => togglePanel(String(plan.day))}
                >
                  <span className="day-badge active">DAY {plan.day}</span>
                  <span className="day-title active">{t(plan.title, plan.title_two)}</span>
                  {showMeals && renderMealBadge(plan.display_meals!)}
                  <DownOutlined className={`stepper-step__chevron${isOpen ? " is-open" : ""}`} />
                </div>

                {isOpen && (
                  <div className="itinerary-tab__day-body">
                    {t(plan.description, plan.description_two) && (
                      <div className="itinerary-modal-body__desc itinerary-tab__description">
                        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                          {t(plan.description, plan.description_two)}
                        </Markdown>
                      </div>
                    )}

                    {/* Activities */}
                    {plan.activities?.length > 0 && (
                      <ul className="activity-list">
                        {plan.activities.map((activity: ItineraryActivity, aidx: number) => {
                          const actTitle = t(activity.title, activity.title_two);
                          const actTagline = t(activity.tagline, activity.tagline_two);
                          const actSubtitle = t(activity.subtitle, activity.subtitle_two);
                          const subtitleLines = actSubtitle
                            .split(/\r?\n/)
                            .map((line) => line.trim())
                            .filter(Boolean);
                          const actImages = getImages(activity);
                          const hasModal = hasActivityModalContent(activity);
                          const isOptional = activity.category === "recommended_optional";

                          return (
                            <li key={aidx} className="activity-list__item">
                              <div className="activity-list__row">
                                <span className="activity-list__bullet" />
                                <span
                                  className={`activity-title${hasModal ? " is-clickable" : " is-plain"}`}
                                  onClick={hasModal ? () => openActivityModal(activity) : undefined}
                                >
                                  {actTitle}
                                </span>
                                {actTagline ? (
                                  <span className="activity-tagline activity-tagline--inline">{actTagline}</span>
                                ) : null}
                                {isOptional && (
                                  <span className="activity-category-tag activity-category-tag--recommended_optional">
                                    Optional
                                  </span>
                                )}
                              </div>
                              {subtitleLines.map((line, sidx) => (
                                <div key={sidx} className="activity-subtitle-row">
                                  <span className="activity-list__bullet activity-list__bullet--sub" />
                                  <span className="activity-subtitle">{line}</span>
                                </div>
                              ))}
                              {actImages.length > 0 && (
                                <div className="image-strip">
                                  {actImages.slice(0, 3).map((src: string, ii: number) => (
                                    <img
                                      key={ii}
                                      src={src}
                                      alt={actTitle}
                                      onClick={() => openActivityModal(activity, ii)}
                                    />
                                  ))}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {/* Hotels */}
                    {plan.hotels?.length > 0 && (
                      <div className="hotel-section">
                        <span className="stay-at-label">{copy.stayAt}</span>
                        {(() => {
                          const primaryHotel = plan.hotels[0];
                          const primaryName = t(primaryHotel.name, primaryHotel.name_two);
                          const primaryStars = parseInt(primaryHotel.category ?? "0", 10) || 0;
                          const primaryImages = getImages(primaryHotel);
                          const hasModal = hasHotelModalContent(primaryHotel);
                          return (
                            <div
                              className={`hotel-card${hasModal ? " is-clickable" : ""}`}
                              onClick={hasModal ? () => openHotelModal(primaryHotel) : undefined}
                            >
                              {primaryImages.length > 0 && (
                                <img
                                  src={primaryImages[0]}
                                  alt={primaryName}
                                  className="hotel-card__thumb"
                                />
                              )}
                              <div className="hotel-card__info">
                                <span className="hotel-card__name">{primaryName}</span>
                                <Stars count={primaryStars} />
                              </div>
                              {hasModal && <span className="hotel-card__arrow">&#8250;</span>}
                            </div>
                          );
                        })()}
                        {plan.hotels.slice(1).map((hotel: ItineraryHotel, hi: number) => {
                          const hasModal = hasHotelModalContent(hotel);
                          return (
                            <span key={hi} className="hotel-alt">
                              {copy.or}{" "}
                              <span
                                className={hasModal ? "hotel-alt__name" : "hotel-alt__name hotel-alt__name--plain"}
                                onClick={hasModal ? () => openHotelModal(hotel) : undefined}
                              >
                                {t(hotel.name, hotel.name_two)}
                              </span>
                            </span>
                          );
                        })}
                        <span className="hotel-alt hotel-alt--plain">{copy.similarClass}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {t(generalContent, generalContentTwo) && (
        <div className="itinerary-modal-body__desc itinerary-tab__content-box itinerary-tab__general-content">
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {t(generalContent, generalContentTwo)}
          </Markdown>
        </div>
      )}

      {additionalTour && (
        <div className="itinerary-modal-body__desc itinerary-tab__content-box">
          <p className="itinerary-tab__content-title">{copy.additionalTour}</p>
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{additionalTour}</Markdown>
        </div>
      )}

      {optionalTour && (
        <div className="itinerary-modal-body__desc itinerary-tab__content-box">
          <p className="itinerary-tab__content-title">{copy.optionalTour}</p>
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{optionalTour}</Markdown>
        </div>
      )}

      {/* Meal code legend */}
      {(hasMOB || hasMGD) && (
        <div className="meal-code-legend">
          <p className="meal-code-legend__title">{copy.mealCodeReference}</p>
          {hasMOB && (
            <p className="meal-code-legend__item"><strong>MOB</strong>{" — "}Meal on Board (机上用餐)</p>
          )}
          {hasMGD && (
            <p className="meal-code-legend__item"><strong>MGD</strong>{" — "}Michelin Guide Dining (米其林指南餐)</p>
          )}
        </div>
      )}

      {/* Special Disclaimer */}
      {t(specialDisclaimer, specialDisclaimerTwo) && (
        <div className="itinerary-tab__disclaimer itinerary-tab__disclaimer--special">
          <p className="itinerary-tab__disclaimer-title">{copy.specialDisclaimer}</p>
          <div className="itinerary-modal-body__desc itinerary-tab__disclaimer--preserve">
            <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {t(specialDisclaimer, specialDisclaimerTwo)}
            </Markdown>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="itinerary-tab__disclaimer">
        <p className="itinerary-tab__disclaimer-title">{copy.disclaimer}</p>
        <ul>
          {copy.disclaimerItems.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>

      {/* Modal */}
      <Modal
        open={!!modal}
        onCancel={() => setModal(null)}
        footer={null}
        centered
        destroyOnClose
        width={600}
        title={modal?.type === "activity" ? copy.activityDetails : copy.hotelDetails}
        className="itinerary-tab__modal"
      >
        {modal && (
          <div className="itinerary-modal-body">
            {/* Title + tagline row */}
            <Flex align="baseline" gap={8} wrap="wrap" style={{ marginBottom: 8 }}>
              <span className="itinerary-modal-body__name">
                {modal.title}
              </span>
              {modal.type === "hotel" && modal.stars != null && modal.stars > 0 && (
                <span className="hotel-card__stars" style={{ fontSize: 13 }}>
                  {Array.from({ length: modal.stars }).map((_, i) => (
                    <StarFilled key={i} style={{ color: "#faad14" }} />
                  ))}
                </span>
              )}
              {modal.tagline && (
                <span className="itinerary-modal-body__tagline">{modal.tagline}</span>
              )}
            </Flex>

            {/* Image carousel */}
            {modal.images.length > 0 && (
              <ImageCarousel images={modal.images} title={modal.title} initialSlide={modal.initialSlide} />
            )}

            {/* Description — rendered as Markdown/RTE */}
            {modal.description && (
              <>
                <Divider style={{ margin: "12px 0" }} />
                <div className="itinerary-modal-body__desc">
                  <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{modal.description}</Markdown>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ItineraryTab;
